/**
 * Notice that this tab is running a build the server has replaced (#2246).
 *
 * A single-page app fetches `index.html` once. After a deploy, a tab that was
 * already open keeps every hashed chunk it loaded — correctly, since those
 * URLs are immutable — so it can run a week-old UI indefinitely with nothing
 * on screen saying so. That is not hypothetical: four fixed-and-deployed bugs
 * were reported as unfixed because the browser reporting them was old, and the
 * code was re-investigated twice before anyone looked at the bundle hash.
 *
 * The probe is deliberately small: re-fetch the id the build emitted, compare
 * it with the one compiled into this bundle, and if they differ raise a flag
 * the UI can act on. It never reloads anything by itself — see `NewBuildBanner`.
 */
import { useEffect, useState } from 'react';

/** Emitted at the `dist/` root by the build (see `vite.config.js`). */
export const BUILD_ID_URL = '/build-id.json';

/** Poll cadence. Slow on purpose: a stale tab is a day-scale problem. */
export const BUILD_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * The id compiled into this bundle. `vite.config.js` substitutes the literal,
 * so this is a constant string by the time the browser sees it.
 */
export const CURRENT_BUILD_ID: string = __BUILD_ID__;

/**
 * Should the operator be offered a reload?
 *
 * Pure, and the only place the decision is made: everything about this feature
 * that can be wrong is a wrong answer HERE, so the cases live in unit tests
 * rather than behind a timer and a network.
 *
 * Every answer that is not "two well-formed ids that differ" is `false`. The
 * asymmetry is deliberate — a missed prompt costs one poll interval, while a
 * wrong prompt teaches operators to ignore the banner, which is the only
 * channel this incident left us.
 */
export function shouldPromptReload(currentId: string, fetchedPayload: unknown): boolean {
  if (currentId === '') return false;
  if (typeof fetchedPayload !== 'object' || fetchedPayload === null) return false;
  const { buildId } = fetchedPayload as { buildId?: unknown };
  if (typeof buildId !== 'string' || buildId === '') return false;
  return buildId !== currentId;
}

/**
 * `true` once the served build id differs from this bundle's, and then forever.
 *
 * It never returns to `false`: the running code cannot become current again,
 * only a reload can fix it, and a banner that flickers away on the next
 * successful poll would be a banner nobody ever manages to click.
 */
export function useBuildVersionCheck(currentId: string = CURRENT_BUILD_ID): boolean {
  const [newBuildAvailable, setNewBuildAvailable] = useState(false);

  useEffect(() => {
    // Dev serves modules live and HMR already keeps the page honest; there is
    // no emitted id to compare against, so the whole chain is off — no fetch,
    // no listener, no timer.
    if (import.meta.env.DEV) return;

    let unmounted = false;

    const check = async (): Promise<void> => {
      let payload: unknown;
      try {
        // `no-store` is load-bearing: a cached probe response would report the
        // old id forever, which is precisely the failure being fixed.
        const response = await fetch(BUILD_ID_URL, { cache: 'no-store' });
        if (!response.ok) return;
        payload = await response.json();
      } catch {
        // SILENCE IS THE CORRECT BEHAVIOUR, not a swallowed error. This runs
        // every five minutes in every open tab; an offline moment, a 502 from
        // a deploy in flight, or an HTML error page parsed as JSON are all
        // normal and say nothing about the build. Reporting them would nag
        // operators with noise they cannot act on — and a failed tick must
        // also leave an already-shown banner shown, which returning here does.
        // Do not "fix" this into a toast.
        return;
      }
      if (unmounted) return;
      if (shouldPromptReload(currentId, payload)) setNewBuildAvailable(true);
    };

    // A tab regaining visibility is the moment that matters most: it is
    // exactly the tab that was left open across a deploy.
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') void check();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const timer = setInterval(() => void check(), BUILD_CHECK_INTERVAL_MS);

    return () => {
      unmounted = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(timer);
    };
  }, [currentId]);

  return newBuildAvailable;
}
